/**
 * Production's pre-TRUS-762 version, kept verbatim for orgs WITHOUT the
 * `member_profile_v2` capability (they keep the previous member profile).
 * Do not add features here; remove once every org is on member_profile_v2.
 */
/**
 * MemberProgramWorkspace — single "Program Activities" workspace for a member.
 *
 * Collapses the previously-separate Program Activities tabs (Activities,
 * Conversations, Documents, Forms, Tasks) AND Case Episodes into one area:
 *   - one enrollment picker (shown when the member has >1 enrollment)
 *   - a work-tab bar; each work-tab renders the same underlying view it did
 *     before (EnrollmentDetailNew in hideChrome mode, TaskList, or the full
 *     MemberCaseEpisodes episode workspace — switcher + profile + history).
 *
 * The Case Episodes work-tab is the case details view (header, profile,
 * history). When the selected program has an open case it is the tab that
 * opens first (TRUS-762); otherwise the first tab in the org's order does.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { membersAPI, Enrollment, Member } from '@/services/api/members';
import { caseEpisodesAPI } from '@/services/api/caseEpisodes';
import { EnrollmentDetailNew } from '@/components/enrollments/EnrollmentDetailLegacy';
import { LetterComposer } from '@/components/communications/LetterComposer';
import { LetterSendHistory } from '@/components/communications/LetterSendHistory';
import MemberCaseEpisodes from './MemberCaseEpisodesLegacy';
import MemberOutreachHistoryTab from './MemberOutreachHistoryTab';
import MemberOutreachTrackingTab from './MemberOutreachTrackingTab';
import TaskList from '@/components/tasks/TaskList';
import { useCapability } from '@/hooks/useCapabilities';

interface Props {
  memberId: number;
  memberName?: string;
  member?: Member;
  onNavigate?: (tab: string, id?: number) => void;
  show?: {
    caseEpisodes?: boolean;
    activities?: boolean;
    conversations?: boolean;
    documents?: boolean;
    forms?: boolean;
    tasks?: boolean;
    mail?: boolean;
  };
  /**
   * Per-org wording and ordering for the work-tab bar, keyed by WorkTab id
   * (TRUS-528). Resolved by the parent from GET /profile-layout; absent or
   * partial means "platform default for that tab", so nothing here is
   * required and an org that configured nothing renders exactly as before.
   *
   * This bar is where "tracking activities" and the case-note surfaces the
   * ticket names actually live, which is why it is configurable at all.
   */
  tabConfig?: Partial<Record<WorkTab, { label?: string; order?: number }>>;
  /** TRUS-685: open this work tab (from a record link), when it is shown. */
  requestedWorkTab?: { tab: WorkTab; at: number } | null;
}

type WorkTab = 'case-episodes' | 'activities' | 'conversations' | 'outreach' | 'documents' | 'forms' | 'tasks' | 'mail';

export default function MemberProgramWorkspace({ memberId, memberName, member, onNavigate, show, tabConfig, requestedWorkTab }: Props) {
  const { t } = useAppTranslation();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showLetterComposer, setShowLetterComposer] = useState(false);

  // Outreach tab mode: billing view (touches + claims + $) when the org has the
  // `outreach_billing` capability, else billing-agnostic touch tracking. Hook must
  // run unconditionally (before any early return), so read the org id here.
  const activeCboId = parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || 0;
  const { enabled: outreachBillingEnabled } = useCapability(activeCboId || null, 'outreach_billing');

  const flags = {
    caseEpisodes: show?.caseEpisodes !== false,
    activities: show?.activities !== false,
    conversations: show?.conversations !== false,
    documents: show?.documents !== false,
    forms: show?.forms !== false,
    tasks: show?.tasks !== false,
    mail: show?.mail !== false,
  };

  const workTabs = useMemo(() => {
    const tabs: Array<{ id: WorkTab; label: string; order: number }> = [];
    // The org's word for a tab wins; otherwise the platform label goes through
    // i18n as it always has. The fallback order is the declaration order below,
    // so an org that reorders only some tabs keeps the rest where they were.
    const push = (id: WorkTab, key: string, en: string) => {
      const cfg = tabConfig?.[id];
      tabs.push({ id, label: cfg?.label || t(key, en), order: cfg?.order ?? (tabs.length + 1) * 10 });
    };
    if (flags.caseEpisodes) push('case-episodes', 'members.caseEpisodes', 'Case Episodes');
    if (flags.activities) push('activities', 'members.activities', 'Activities');
    if (flags.conversations) push('conversations', 'members.conversations', 'Conversations');
    if (flags.conversations) push('outreach', 'members.outreachHistory', 'Outreach');
    if (flags.documents) push('documents', 'members.documents', 'Documents');
    if (flags.forms) push('forms', 'members.forms', 'Forms');
    if (flags.mail) push('mail', 'members.mailLetters', 'Mail / Letters');
    if (flags.tasks) push('tasks', 'members.tasks', 'Tasks');
    return tabs.sort((a, b) => a.order - b.order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flags.caseEpisodes, flags.activities, flags.conversations, flags.documents, flags.forms, flags.mail, flags.tasks, tabConfig]);

  const [workTab, setWorkTab] = useState<WorkTab>(workTabs[0]?.id || 'activities');
  // Once someone picks a tab (or a record link asks for one), the open-case
  // default below no longer moves them.
  const tabChosen = useRef(false);
  const chooseTab = (tab: WorkTab) => {
    tabChosen.current = true;
    setWorkTab(tab);
  };

  useEffect(() => {
    if (requestedWorkTab && workTabs.some((w) => w.id === requestedWorkTab.tab)) chooseTab(requestedWorkTab.tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedWorkTab]);

  useEffect(() => {
    // Keep the active work-tab valid if the visible set changes.
    if (workTabs.length && !workTabs.some((w) => w.id === workTab)) setWorkTab(workTabs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workTabs]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    membersAPI
      .getMemberEnrollments(memberId)
      .then((rows) => {
        if (cancelled) return;
        const sorted = (rows || []).sort((a, b) => {
          if (a.status === 'active' && b.status !== 'active') return -1;
          if (b.status === 'active' && a.status !== 'active') return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        setEnrollments(sorted);
        if (sorted.length > 0) setSelectedId((prev) => prev ?? sorted[0].id);
      })
      .catch(() => {
        if (!cancelled) setEnrollments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  // When the selected program has an open case, open the case details view.
  const selectedProgramId = enrollments.find((e) => e.id === selectedId)?.program_id;
  useEffect(() => {
    if (!selectedProgramId || !flags.caseEpisodes || tabChosen.current) return;
    let cancelled = false;
    caseEpisodesAPI
      .listEpisodes({ member_id: memberId, program_id: selectedProgramId })
      .then((eps) => {
        const open = (eps || []).some((e) => e.is_current && ['NEW', 'ACTIVE', 'SUSPENDED'].includes(e.status));
        if (!cancelled && open && !tabChosen.current) setWorkTab('case-episodes');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [memberId, selectedProgramId, flags.caseEpisodes]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('members.loadingEnrollments', 'Loading enrollments…')}
      </div>
    );
  }

  const selected = enrollments.find((e) => e.id === selectedId) || null;
  // Tasks and Outreach history are member-scoped; everything else needs an enrollment.
  const needsEnrollment = workTab !== 'tasks' && workTab !== 'outreach';
  const cboId = activeCboId;

  return (
    <div className="space-y-4">
      {/* Enrollment picker (only when there are enrollments and the current
          work-tab is enrollment-scoped). */}
      {enrollments.length > 0 && needsEnrollment && (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('members.programEnrollment', 'Program enrollment:')}
          </label>
          <Select value={selectedId ? String(selectedId) : ''} onValueChange={(v) => setSelectedId(Number(v))}>
            <SelectTrigger className="w-80">
              <SelectValue placeholder={t('members.selectEnrollment', 'Select an enrollment')} />
            </SelectTrigger>
            <SelectContent>
              {enrollments.map((e) => (
                <SelectItem key={e.id} value={String(e.id)}>
                  <span className="flex items-center gap-2">
                    {e.program_name || `Program #${e.program_id}`}
                    <Badge variant={e.status === 'active' ? 'default' : 'secondary'} className="ml-1">
                      {e.status}
                    </Badge>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Work-tab bar */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
        {workTabs.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => chooseTab(w.id)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              workTab === w.id
                ? 'bg-v2accent text-white'
                : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {workTab === 'tasks' ? (
        <TaskList memberId={memberId} memberName={memberName || 'Member'} />
      ) : workTab === 'outreach' ? (
        outreachBillingEnabled ? (
          <MemberOutreachHistoryTab memberId={memberId} />
        ) : (
          <MemberOutreachTrackingTab memberId={memberId} memberName={memberName} enrollments={enrollments} />
        )
      ) : enrollments.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p className="text-sm">{t('members.noEnrollmentsFound', 'No enrollments found for this member.')}</p>
          <p className="text-xs mt-1">{t('members.enrollMemberFirst', 'Enroll the member in a program first.')}</p>
        </div>
      ) : !selected ? null : workTab === 'case-episodes' ? (
        <MemberCaseEpisodes
          memberId={memberId}
          programId={selected.program_id}
          cboId={cboId}
          enrollmentId={selected.id}
          onNavigate={onNavigate}
        />
      ) : workTab === 'mail' ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              {t('members.mailLetters', 'Mail / Letters')}
              <span className="ml-2 text-xs font-normal text-gray-500">
                {selected.program_name || `Program #${selected.program_id}`}
              </span>
            </h3>
            <Button size="sm" onClick={() => setShowLetterComposer(true)}>
              <Mail className="w-4 h-4 mr-2" /> {t('members.sendLetter', 'Send Letter')}
            </Button>
          </div>
          {/* Scoped to this enrollment (→ current case episode) for tracking. */}
          <LetterSendHistory memberId={memberId} programId={selected.program_id} enrollmentId={selected.id} />
          {showLetterComposer && (
            <LetterComposer
              memberId={memberId}
              memberName={memberName}
              memberAddress={
                member
                  ? {
                      street_address: member.street_address || '',
                      city: member.city || '',
                      state: member.state || '',
                      zip_code: member.zip_code || '',
                    }
                  : undefined
              }
              programId={selected.program_id}
              enrollmentId={selected.id}
              onClose={() => setShowLetterComposer(false)}
              onSent={() => setShowLetterComposer(false)}
            />
          )}
        </div>
      ) : (
        <EnrollmentDetailNew
          key={`${selected.id}-${workTab}`}
          enrollmentId={selected.id}
          defaultTab={workTab}
          hideChrome
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}
